/**
 * RecordOutcomeModal - record outcome (funded/passed/stalled/withdrawn) for a pipeline.
 */
import React, { useState } from 'react';
import { Loader2, X, CheckCircle2 } from 'lucide-react';
import {
  PassReason,
  PipelineOutcome,
  RecordOutcomeRequest,
} from '../types/dealPipeline';

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: RecordOutcomeRequest) => Promise<void> | void;
}

const OUTCOME_OPTIONS: Array<{ value: PipelineOutcome; label: string; color: string }> = [
  { value: 'funded', label: 'Funded', color: 'border-green-300 text-green-700 hover:bg-green-50' },
  { value: 'passed', label: 'Passed', color: 'border-red-300 text-red-700 hover:bg-red-50' },
  { value: 'stalled', label: 'Stalled', color: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'border-gray-300 text-gray-700 hover:bg-gray-50' },
];

const PASS_REASONS: Array<{ value: PassReason; label: string }> = [
  { value: 'team', label: 'Team' },
  { value: 'market', label: 'Market' },
  { value: 'product', label: 'Product' },
  { value: 'traction', label: 'Traction' },
  { value: 'valuation', label: 'Valuation' },
  { value: 'terms', label: 'Terms' },
  { value: 'timing', label: 'Timing' },
  { value: 'stage_mismatch', label: 'Stage Mismatch' },
  { value: 'sector_mismatch', label: 'Sector Mismatch' },
  { value: 'geography', label: 'Geography' },
  { value: 'thesis_mismatch', label: 'Thesis Mismatch' },
  { value: 'portfolio_conflict', label: 'Portfolio Conflict' },
  { value: 'other', label: 'Other' },
];

const RecordOutcomeModal: React.FC<Props> = ({ open, onClose, onConfirm }) => {
  const [outcome, setOutcome] = useState<PipelineOutcome>('funded');
  const [passReason, setPassReason] = useState<PassReason | ''>('');
  const [passNotes, setPassNotes] = useState('');
  const [fundedAmount, setFundedAmount] = useState<string>('');
  const [fundedInstrument, setFundedInstrument] = useState('');
  const [fundedTerms, setFundedTerms] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    const payload: RecordOutcomeRequest = { outcome };

    if (outcome === 'passed') {
      if (passReason) payload.pass_reason = passReason;
      if (passNotes.trim()) payload.pass_notes = passNotes.trim();
    }
    if (outcome === 'funded') {
      const amt = parseFloat(fundedAmount);
      if (!Number.isNaN(amt) && amt > 0) payload.funded_amount = amt;
      if (fundedInstrument.trim()) payload.funded_instrument = fundedInstrument.trim();
      if (fundedTerms.trim()) payload.funded_terms = fundedTerms.trim();
    }
    if ((outcome === 'stalled' || outcome === 'withdrawn') && passNotes.trim()) {
      payload.pass_notes = passNotes.trim();
    }

    setSubmitting(true);
    try {
      await onConfirm(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Record Outcome</h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-2">
            Outcome
          </label>
          <div className="grid grid-cols-2 gap-2">
            {OUTCOME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setOutcome(opt.value)}
                className={`px-3 py-2 text-sm rounded-lg border ${
                  outcome === opt.value
                    ? `bg-blue-600 text-white border-blue-600`
                    : `bg-white ${opt.color}`
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {outcome === 'passed' && (
          <>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Pass Reason
              </label>
              <select
                value={passReason}
                onChange={(e) => setPassReason(e.target.value as PassReason)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select reason...</option>
                {PASS_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Pass Notes
              </label>
              <textarea
                value={passNotes}
                onChange={(e) => setPassNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Optional context"
              />
            </div>
          </>
        )}

        {outcome === 'funded' && (
          <>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Funded Amount (USD)
              </label>
              <input
                type="number"
                value={fundedAmount}
                onChange={(e) => setFundedAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="500000"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Instrument
              </label>
              <input
                type="text"
                value={fundedInstrument}
                onChange={(e) => setFundedInstrument(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="SAFE, Equity, Convertible Note..."
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                Terms
              </label>
              <textarea
                value={fundedTerms}
                onChange={(e) => setFundedTerms(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Cap, discount, valuation, board seat..."
              />
            </div>
          </>
        )}

        {(outcome === 'stalled' || outcome === 'withdrawn') && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Notes
            </label>
            <textarea
              value={passNotes}
              onChange={(e) => setPassNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Optional context"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Record
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordOutcomeModal;
